import React, { useCallback, useEffect, useState } from 'react';
import { Box, Heading, Text, Image } from '@resideo/blueprint-react';
import { resideoBlackLogo } from 'assets/images';
import { TableHeader } from 'components/common/Styles';
import _ from 'lodash';
import styled from 'styled-components';
import SeparatorLine from 'components/common/SeparatorLine';
import { convertMoneyAmount } from 'routes/ServicesAndSubscriptions/Cart';
import { useTranslation } from 'react-i18next';
import { graphql } from 'react-relay/hooks';
import moment from 'moment';
import { useParams } from 'react-router';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import PageLoading from 'components/common/PageLoading';
import { InvoiceQuery } from './__generated__/InvoiceQuery.graphql';
import { subscriptionOrderProducts } from 'data/SelectPlanData';
import { COUNTRIES } from 'config/countries';
import Subscription from 'routes/ServicesAndSubscriptions/SBApiSubscriptions';

const Container = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 50px 50px 0px 50px;
`;

const TitleInvoice = styled.h1`
  margin-bottom: 0;
  font-family: 'URW_Din_CondBold';
  font-size: 45px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: 2px;
`;

const InvoiceNo = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  letter-spacing: 0;
`;

const BillTo = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  letter-spacing: 0;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textHeading};
  padding-top: 1.5rem;
`;

const BillToAddress = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  letter-spacing: 0;
  color: ${({ theme }) => theme.colors.textHeading};
`;

const TextDue = styled(Text)`
  margin-top: 50px;
  color: ${({ theme }) => theme.colors.textHeading};
  font-size: ${({ theme }) => theme.fontSizes.small};
  letter-spacing: 0;
`;

const TableItems = styled.table`
  margin-top: 10px;
  th {
    font-size: ${({ theme }) => theme.fontSizes.small} !important;
    &:last-child {
      text-align: right;
    }
  }
  td {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    letter-spacing: 0;
    color: ${({ theme }) => theme.colors.textHeading};
    &:nth-last-child(2) {
      color: ${({ theme }) => theme.colors.blackText};
    }
    &:last-child {
      text-align: right;
      color: ${({ theme }) => theme.colors.text};
      font-weight: ${({ theme }) => theme.fontWeights.bold};
    }
  }
`;

const SubtotalBox = styled.div`
  padding: 15px 25px;
`;

const FooterNote = styled.p`
  font-family: 'Roboto';
  margin-top: 140px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.textHeading};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-style: italic;
  letter-spacing: 0;
  line-height: 21.77px;
  text-align: center;
`;

const ServiceLocationTd = styled.td`
  color: ${({ theme }) => theme.colors.blackText};
  font-family: 'Roboto';
  font-size: ${({ theme }) => theme.fontSizes.medium};
`;

const Invoice = () => {
  const params = useParams() as any;
  const invoiceId = params?.id;
  const subscriptionId = params?.subid;
  const invoiceNumber = params?.invoiceNumber;
  const api = new Subscription(subscriptionId, invoiceNumber);
  const [subBillingQty, setSubBillingQty] = useState<Array<number>>([]) as any;

  useEffect(() => {
    api.fetchDROrderDetails().then(res => {
      // Sort the response by start date
      const sortQty = _.orderBy(res?.items, item => {
        if (
          item &&
          item.metadata &&
          item.metadata.ratingPeriod &&
          item.metadata.ratingPeriod.start
        ) {
          return item.metadata.ratingPeriod.start;
        }
      });
      setSubBillingQty(sortQty);
    });
  }, []);

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const environment = useRelayEnvironment();
  const { t } = useTranslation();
  const countryCodeToFullText = code => {
    let countryText = '';
    COUNTRIES.map(obj => {
      if (obj.value === code) {
        countryText = t(obj.text);
      }
    });
    return countryText;
  };

  const dateSort = (data: Array<any>) => {
    return _.orderBy(data, item => item.billingPeriodStartDate);
  };

  console.log(subBillingQty);

  const invoiceData = useCallback(() => {
    if (invoiceId !== '' && invoiceId !== undefined) {
      fetchQuery<InvoiceQuery>(
        environment,
        graphql`
          query InvoiceQuery($invoiceId: ID!) {
            invoice: node(id: $invoiceId) {
              ... on DigitalRiverOrder {
                billTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                createdAt
                currency
                id
                invoiceNumber
                lineItems {
                  sku
                  quantity
                  amount {
                    amount
                    currencyCode
                  }
                  billingPeriodStartDate
                  billingPeriodEndDate
                  deviceNumber
                }
                shipTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                subTotal {
                  amount
                  currencyCode
                }
                tax {
                  amount
                  currencyCode
                }
                taxRate
                total {
                  amount
                  currencyCode
                }
                totalShipping {
                  amount
                  currencyCode
                }
              }
            }
          }
        `,
        {
          invoiceId,
        }
      ).subscribe({
        next: data => {
          const theOrder = {
            ...data?.invoice,
            lineItems: data?.invoice?.lineItems?.map(ele => {
              // convert amount to 'cents', then use Math.round and toFixed to avoid rounding errors
              const centsAmount = ele?.amount?.amount
                ? Math.round(parseFloat(`${ele.amount.amount}`) * 100)
                : 0;
              const qty = ele?.quantity ?? 1;
              const price = (Math.round(centsAmount / qty) / 100).toFixed(2);
              return {
                ...ele,
                ...(subscriptionOrderProducts.find(
                  item => item.sku === ele?.sku
                ) ?? {
                  name: 'Product Not Found',
                  type: 'Unknown',
                }),
                price,
              };
            }),
          };
          setData({ invoice: theOrder });
          setLoading(false);
        },
        error: () => {
          setData({});
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
      setData({});
    }
  }, [environment, invoiceId]);

  useEffect(() => {
    invoiceData();
  }, [invoiceId]);

  return isLoading || data === null ? (
    <PageLoading />
  ) : (
    <Container>
      <Image src={resideoBlackLogo} alt='resideo' />
      <TitleInvoice>{t('invoiceService.title')}</TitleInvoice>
      <InvoiceNo>
        <strong>{t('invoiceService.invoiceNo')} :</strong>{' '}
        {data?.invoice?.invoiceNumber}
      </InvoiceNo>
      <BillTo>{t('invoiceService.billTo')}:</BillTo>
      <BillToAddress>
        {data?.invoice?.billTo?.firstName} {data?.invoice?.billTo?.lastName}
        <br />
        {data?.invoice?.billTo?.organization ??
          data?.invoice?.shipTo?.organization}
        <br />
        {data?.invoice?.billTo?.address?.addressLine1}
        <br />
        {data?.invoice?.billTo?.address?.city},{' '}
        {data?.invoice?.billTo?.address?.stateProvinceRegionCode}{' '}
        {data?.invoice?.billTo?.address?.zipPostalCode}
        <br />
        {t(countryCodeToFullText(data?.invoice?.billTo?.address?.countryCode))}
      </BillToAddress>
      <TextDue>{t('invoiceService.chargedMsg')}</TextDue>
      <TableItems className='table-responsive'>
        <thead>
          <tr>
            <TableHeader>{t('invoiceService.date')}</TableHeader>
            <TableHeader>{t('invoiceService.description')}</TableHeader>
            <TableHeader>{t('invoiceService.billingPeriod')}</TableHeader>
            <TableHeader>{t('invoiceService.monitors')}*</TableHeader>
            <TableHeader>{t('invoiceService.amount')}</TableHeader>
          </tr>
        </thead>
        <tbody>
          {dateSort(data?.invoice?.lineItems).map((invoice, i) => (
            <tr key={i}>
              <td>{moment(data?.invoice?.createdAt).format('MMM DD, YYYY')}</td>
              {/* <td>{showDesc(invoice?.sku)}</td> */}
              <td>
                {invoice.name.split(' ')[0]}
                <br />
                {invoice.name.split(' ')[1]} {invoice.name.split(' ')[2]}
              </td>
              <td>
                {invoice.billingPeriodStartDate ?? 'N/A'} -{' '}
                {invoice.billingPeriodEndDate ?? 'N/A'}
              </td>
              <ServiceLocationTd>
                {/* {parseFloat(invoice?.total?.amount ?? '0') > 0
                  ? invoice.quantity
                  : '0'} */}
                {subBillingQty.length > 0 &&
                subBillingQty[i].metadata &&
                subBillingQty[i]?.metadata.subBillQty
                  ? subBillingQty[i]?.metadata.subBillQty
                  : 0}
              </ServiceLocationTd>
              <td color='#000000'>
                {convertMoneyAmount(
                  {
                    amount: invoice?.price,
                    currencyCode: invoice?.amount?.currencyCode,
                  },
                  'en-us'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TableItems>
      <SeparatorLine />
      <SubtotalBox>
        <Box marginBottom='small'>
          <Box
            display='flex'
            width='100%'
            justifyContent='flex-end'
            paddingBottom='small'>
            <Text width='calc(100% - 7.625rem)' textAlign='right'>
              {t('invoiceService.subTotalInUSD')}
            </Text>
            <Text width='7.625rem' textAlign='right'>
              {convertMoneyAmount(
                {
                  amount: data?.invoice?.subTotal?.amount,
                  currencyCode: data?.invoice?.subTotal?.currencyCode,
                },
                'en-us'
              )}
            </Text>
          </Box>
          <Box display='flex' width='100%' justifyContent='flex-end'>
            <Text width='calc(100% - 7.625rem)' textAlign='right'>
              {t('invoiceService.salesTax')}{' '}
              {'(' +
                (parseFloat(`${data.invoice.taxRate}`) * 100).toFixed(2) +
                '%' +
                ')'}
            </Text>
            <Text width='7.625rem' textAlign='right'>
              {convertMoneyAmount(
                {
                  amount: data?.invoice?.tax?.amount,
                  currencyCode: data?.invoice?.tax?.currencyCode,
                },
                'en-us'
              )}
            </Text>
          </Box>
        </Box>
        <Box>
          <Box
            display='flex'
            alignItems='center'
            width='100%'
            justifyContent='flex-end'>
            <Heading
              letterSpacing='normal'
              fontSize='16px'
              width='calc(100% - 7.625rem)'
              textAlign='right'>
              {t('invoiceService.totalInUSD')}
            </Heading>
            <Heading
              letterSpacing='normal'
              width='7.625rem'
              fontSize='16px'
              textAlign='right'>
              {convertMoneyAmount(
                {
                  amount: data?.invoice?.total?.amount,
                  currencyCode: data?.invoice?.total?.currencyCode,
                },
                'en-us'
              )}
            </Heading>
          </Box>
        </Box>
      </SubtotalBox>
      <FooterNote>
        * {t('invoiceService.footerMsg1')}
        <br />
        <strong>{t('invoiceService.footerMsg2')}</strong>{' '}
        {t('invoiceService.footerMsg3')}
      </FooterNote>
    </Container>
  );
};

export default Invoice;
